import React, { createContext, useContext, useEffect, useState } from 'react';
import { applyBrand, DEFAULT_BRAND, labelFor, mergeBrand, readCachedBrand, writeCachedBrand } from './brand';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BrandContext = createContext(DEFAULT_BRAND);

/** Wraps the whole app (src/index.js), the mobile shell included: one brand for every screen. */
export function BrandProvider({ children, initial }) {
  const [brand, setBrand] = useState(() => initial || readCachedBrand() || DEFAULT_BRAND);

  useEffect(() => { applyBrand(brand); }, [brand]);

  useEffect(() => {
    if (initial) return undefined;
    let live = true;
    fetch(`${API_URL}/api/platform/brand`)
      .then((r) => (r.ok ? r.json() : null))
      .then((answer) => {
        if (!live || !answer) return;
        writeCachedBrand(answer);
        setBrand(mergeBrand(answer));
      })
      .catch(() => { /* no server: keep the last brand or the vendor's */ });
    return () => { live = false; };
  }, [initial]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}

/** The flavour's word for a screen or workspace, or the built-in one. */
export function useLabel() {
  const brand = useBrand();
  return (id, fallback) => labelFor(brand, id, fallback);
}

/** Mark and product name together: the sign-in, reset and landing pages. */
export function BrandLockup({ dark = false }) {
  const { brand } = useBrand();
  return (
    <span className="inline-flex items-center gap-2">
      <BrandMark size="w-10 h-10" rounded="rounded-lg" />
      <span className={`font-display font-bold text-xl ${dark ? 'text-white' : 'text-ink'}`} data-testid="brand-name">
        {brand.product_name}
      </span>
    </span>
  );
}

/** "Powered by FoodOps" under a client's own brand, when its flavour asks for it. */
export function PoweredBy({ className = '' }) {
  const { id, brand } = useBrand();
  if (!brand.powered_by || id === 'vendor') return null;
  return <p className={`text-xs text-ink-subtle ${className}`} data-testid="brand-powered-by">Powered by FoodOps</p>;
}

/** The brand mark: the logo when there is one, else the icon letter on the primary colour. */
export function BrandMark({ size = 'w-9 h-9', rounded = 'rounded-md', letterClass = 'text-lg' }) {
  const { brand } = useBrand();
  if (brand.logo_url) {
    return <img src={brand.logo_url} alt={brand.product_name} className={`${size} ${rounded} object-contain`} data-testid="brand-logo-img" />;
  }
  return (
    <div className={`${size} ${rounded} bg-primary-500 flex items-center justify-center`} aria-hidden="true" data-testid="brand-mark">
      <span className={`text-surface font-display font-bold ${letterClass}`}>{brand.icon_letter || 'F'}</span>
    </div>
  );
}
